// Fleet Reports — Idling Time section (P2, 2026-07-16).
// The backend sends idling in MINUTES with weeklyDisplayDivisor:60. Mirroring
// the xlsx: the WEEKLY figures render in HOURS (÷60), the DAILY series stays in
// MINUTES. The week is deliberately NOT the sum of the days (cross-midnight idle
// runs) — that is the backend's business; here we just display what it sends.
import React from "react";
import { Card, Grid } from "@mui/material";
import FleetBar from "./charts/FleetBar";
import FleetLine from "./charts/FleetLine";
import StatTile from "./StatTile";
import { RAG, ragForSeries, ragBg } from "./ragColors";

const VEHICLE_COLORS = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

const fmt = (v, d = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? "No data" : Number(v).toFixed(d);

const sectionTitle = (text) => (
  <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
    {text}
  </p>
);

export default function IdlingSection({ report }) {
  const m = (report && report.metrics && report.metrics.idling) || null;
  const div = (m && m.weeklyDisplayDivisor) || 60;
  const toHours = (v) => (v === null || v === undefined || Number.isNaN(v) ? null : v / div);

  // Weekly rows converted to hours (lower is better).
  const weekRows = React.useMemo(
    () => ((m && m.perVehicleWeek) || []).map((r) => ({ ...r, value: toHours(r.value) })),
    [m] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const ragWeek = React.useMemo(() => ragForSeries(weekRows, false), [weekRows]);

  const barData = React.useMemo(
    () => weekRows.map((r) => ({ key: r.sensorId, label: r.name || r.sensorId, value: r.value, color: ragWeek(r.value) })),
    [weekRows, ragWeek]
  );

  // Daily series stays in MINUTES.
  const days = React.useMemo(() => ((m && m.fleetDay) || []).map((d) => d.date), [m]);
  const lineSeries = React.useMemo(() => {
    if (!m) return [];
    return ((report && report.vehicles) || []).map((v, i) => ({
      key: v.sensorId,
      label: v.name || v.sensorId,
      color: VEHICLE_COLORS[i % VEHICLE_COLORS.length],
      points: (m.perVehicleDay || [])
        .filter((r) => r.sensorId === v.sensorId)
        .reduce((acc, r) => {
          acc[r.date] = r.value;
          return acc;
        }, {}),
    }));
  }, [m, report]);

  if (!m) return null;

  return (
    <div>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={4} md={3}>
          <StatTile label="Fleet Idling Time" value={toHours(m.fleetWeek)} unit="h" better="lower" decimals={2} />
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle("Per vehicle (h)")}
            <FleetBar data={barData} unit="h" height="240px" />
          </Card>
        </Grid>
      </Grid>

      <Card style={{ padding: "16px", marginBottom: "20px" }}>
        {sectionTitle("Daily per vehicle (min)")}
        <FleetLine days={days} series={lineSeries} unit="min" height="300px" />
      </Card>

      <Card style={{ padding: "16px" }}>
        {sectionTitle("Totals (h)")}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#e2e2e2" }}>
                <th style={{ textAlign: "left", padding: "10px" }}>Vehicle</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Total (h)</th>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((r) => {
                const c = ragWeek(r.value);
                return (
                  <tr key={r.sensorId} style={{ borderBottom: "1px solid #eeeeee" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{r.name}</td>
                    <td style={{ padding: "10px", textAlign: "right", backgroundColor: ragBg(c), color: r.value === null ? RAG.neutral : "#333333" }}>
                      {fmt(r.value)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
                <td style={{ padding: "10px" }}>Fleet</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{fmt(toHours(m.fleetWeek))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "13px", color: "grey", marginTop: "12px", marginBottom: 0 }}>
          <b>Hint:</b> Only idling beyond the first 5 minutes of each stop is counted. The total is
          measured across the whole selected period, so it can exceed the sum of the daily figures when an idle spans midnight.
        </p>
      </Card>
    </div>
  );
}
